import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormHelperText,
  Grid,
  Autocomplete,
  Tooltip,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ModalTemplate from "../Common/ModalTemplate";
import AppButton from "../AppButton";
import theme from "../../utils/theme";
import IconButtons from "../IconButtons";
import ImageUploadSection from "../CreateRFI/ImageUploadSection";
import useCreateEditComponentContent from "../../hooks/useCreateEditComponentContent";
import { InfoOutlined } from "@mui/icons-material";

const styles = {
  dialogPaper: {
    borderRadius: 3,
    bgcolor: theme.palette.background.default,
    boxShadow: 10,
    width: 960,
    maxWidth: "98vw",
    maxHeight: "99vh",
    mt: 8,
  },
  dialogTitle: {
    bgcolor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    fontSize: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    pr: 1.5,
    minHeight: 56,
  },
  dialogContent: {
    maxHeight: "75vh",
    overflowY: "auto",
    overflowX: "hidden",
    p: "1rem",
    pt: "1rem",
  },
  imageUploadBox: {
    // height: { xs: 340, md: 200 },
    p: 2,
    bgcolor: theme.palette.background.paper,
    border: `${2.5}px dashed ${theme.palette.info.main}`,
    borderRadius: 3,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    mb: 2,
    mx: 2,
    mt: 2,
    position: "relative",
    transition: "border-color 0.2s, background-color 0.2s",
    boxShadow: 2,
    "&:hover": {
      borderColor: theme.palette.info.dark,
      bgcolor: theme.palette.background.default,
    },
  },
  itemGroupContainer: {
    mb: 3,
    position: "relative",
    bgcolor: theme.palette.background.default,
    borderRadius: 2,
    border: `${1.5}px solid ${theme.palette.grey[500]}`,
    boxShadow: 0,
    p: 2,
  },
  deleteButton: {
    color: theme.palette.error.main,
    background: theme.palette.background.paper,
    border: `1.5px solid ${theme.palette.grey[500]}`,
    borderRadius: 2,
    boxShadow: 1,
    m: 0.5,
    width: 28,
    height: 28,
    transition: "background 0.2s, color 0.2s",
    "&:hover": {
      background: theme.palette.error.light,
      color: theme.palette.primary.dark,
      borderColor: theme.palette.error.main,
    },
  },
  addButton: {
    color: theme.palette.info.main,
    background: theme.palette.background.paper,
    border: `1.5px solid ${theme.palette.grey[500]}`,
    borderRadius: 2,
    width: 28,
    height: 28,
    boxShadow: 1,
    transition: "background 0.2s, color 0.2s",
    "&:hover": {
      background: theme.palette.info.light,
      color: theme.palette.info.dark,
      borderColor: theme.palette.info.main,
    },
  },
  textField: {
    bgcolor: theme.palette.background.paper,
    "& .MuiInputBase-input": {
      height: 24,
      padding: "8px 12px",
    },
  },
  componentNameLabel: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: 16,
    width: 160,
  },
  dimensionsTitle: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: 18,
    letterSpacing: 0.1,
    textAlign: "left",
    mr: 2,
  },
  itemTextField: {
    bgcolor: theme.palette.background.paper,
    width: 155,
    minWidth: 155,
    maxWidth: 155,
    "& .MuiInputBase-input": {
      height: 24,
      padding: "4px 8px",
    },
    mt: 0.5,
  },
  imagePreviewContainer: {
    width: "100%",
    height: "120px",
    minWidth: "120px",
    position: "relative",
    borderRadius: 1,
    border: `1px solid ${theme.palette.grey[300]}`,
    overflow: "hidden",
    bgcolor: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imagePreview: {
    width: { xs: "100px", md: "300px" },
    height: { xs: "100px", md: "150px" },
    objectFit: "contain",
    borderRadius: 2,
  },
  imageRemoveButton: {
    position: "absolute",
    top: 8,
    right: 8,
    background: theme.palette.background.paper,
    color: theme.palette.primary.main,
    border: `1.5px solid ${theme.palette.grey[500]}`,
    borderRadius: 1,
    zIndex: 2,
    p: 0.5,
  },
  itemLabel: {
    fontWeight: 600,
    mb: 0.5,
  },
  divider: {
    display: "block",
    borderBottom: `1.5px solid ${theme.palette.grey[500]}`,
    width: "100%",
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.75rem",
    ml: 1,
  },
  imageUploadError: {
    borderColor: theme.palette.error.dark,
    bgcolor: "#fff5f5",
  },
  selectField: {
    bgcolor: theme.palette.background.paper,
    "& .MuiInputBase-input": {
      height: 24,
      padding: "8px 12px",
    },
    width: "100%",
  },
};

const CreateTemplateForm = ({
  open,
  onClose,
  initialData,
  onSave,
  isEditing = false,
  isTemplateCreation = false,
  title = "Create Component",
}) => {
  const {
    formik,
    imageFiles,
    isSubmitting,
    handleImageUpload,
    removeImage,
    addItem,
    removeItem,
    addSubPart,
    removeSubPart,
    handleClose,
    handleModalChange,
    models,
    changeTitleFcn,
    changeItemNameFcn,
    changeSubpartFcn,
    checkNewItems,
    checkNewSubparts,
    // handleDeleteItemAPI,
    // debouncedApiRefDeleteSubpart,
  } = useCreateEditComponentContent(
    isTemplateCreation,
    onClose,
    initialData,
    onSave,
    open
  );

  const renderActions = () => {
    if (!isEditing || checkNewItems || checkNewSubparts) {
      return (
        <>
          <AppButton
            buttonType="greyed"
            onClick={isEditing ? handleClose : onClose}
            sx={{ mx: 1 }}
            label="Close"
          />
          <AppButton
            buttonType="primary"
            onClick={formik.handleSubmit}
            sx={{ minWidth: 120, mx: 1 }}
            label={isEditing ? "Update" : "Save"}
            isLoading={isSubmitting}
          />
        </>
      );
    }
    return null;
  };

  const PaperComponent = (props) => (
    <Paper {...props}>
      <Box
        sx={{
          p: 1,
          bgcolor: "grey.100",
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <InfoOutlined fontSize="small" color="error" />
        <Typography variant="caption" color="error">
          All models are assigned to a template.
        </Typography>
      </Box>
      {props.children}
    </Paper>
  );

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title={isEditing ? "Edit Component" : title}
      titleIcon={<AddIcon />}
      actions={renderActions()}
      showActions={!isEditing || checkNewItems || checkNewSubparts}
      footerNote={
        isEditing && !checkNewItems && !checkNewSubparts
          ? "Updates made to existing template items are saved automatically."
          : null
      }
    >
      {isTemplateCreation && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography variant="subtitle1" sx={styles.componentNameLabel}>
            Model:
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Autocomplete
              options={models}
              getOptionLabel={(option) => option.model_name}
              getOptionDisabled={(option) => option.is_template_created}
              value={
                models?.find(
                  (modal) => modal?.model_id === formik.values.model_id
                ) || null
              }
              onChange={handleModalChange}
              PaperComponent={
                models?.filter((md) => !md?.is_template_created).length > 0
                  ? null
                  : PaperComponent
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  error={Boolean(formik.errors.model_id)}
                  helperText={formik.errors.model_id}
                  required
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.model_id === value.model_id
              }
              sx={styles.selectField}
            />
            {/* <FormHelperText
              sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <InfoOutlined fontSize="small" />
              Models with created templates are disabled and cannot be selected
            </FormHelperText> */}
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="subtitle1" sx={styles.componentNameLabel}>
          Component name:
        </Typography>
        <Box sx={{ flex: 1 }}>
          <TextField
            size="small"
            name="component_name"
            value={formik.values.component_name || ""}
            onChange={
              initialData?.component_id
                ? (e) => changeTitleFcn(e.target.value)
                : formik.handleChange
            }
            variant="outlined"
            sx={{ flex: 1, ...styles.textField }}
            error={
              formik.touched.component_name &&
              Boolean(formik.errors.component_name)
            }
            required
            fullWidth
          />
          {formik.touched.component_name && formik.errors.component_name && (
            <FormHelperText sx={styles.errorText}>
              {formik.errors.component_name}
            </FormHelperText>
          )}
        </Box>
      </Box>
      <ImageUploadSection
        images={imageFiles || []}
        setImages={formik.setFieldValue}
        error={formik.errors.images}
        handleImageUpload={handleImageUpload}
        handleRemoveImage={removeImage}
      />

      <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 1 }}>
        <Typography variant="subtitle1" sx={styles.dimensionsTitle}>
          Template items
        </Typography>
        <Box sx={{ flex: 1, ml: 1 }}>
          <span style={styles.divider} />
        </Box>
      </Box>

      <Box sx={{ mt: 3, mb: 2 }}>
        {formik.values.items &&
          formik.values.items.map((group, groupIdx) => (
            <Box key={groupIdx} sx={styles.itemGroupContainer}>
              {formik.values.items.length > 1 && (
                <Tooltip title="Delete Item Group" arrow placement="top">
                  <IconButtons
                    buttonType="close"
                    onClick={() => removeItem(groupIdx, group.item_id)}
                    sx={{ float: "right" }}
                  />
                </Tooltip>
              )}

              <Grid container spacing={2} mt={2}>
                <Grid item size={{ xs: 12, md: 2.5 }}>
                  <Typography variant="body2" sx={styles.itemLabel}>
                    Item
                  </Typography>
                  <Box>
                    <TextField
                      size="small"
                      name={`items[${groupIdx}].item_name`}
                      value={group.item_name || ""}
                      onChange={
                        initialData?.component_id
                          ? (e) => {
                              formik.setFieldValue(
                                `items[${groupIdx}].item_name`,
                                e.target.value
                              );
                              group.item_id &&
                                changeItemNameFcn(
                                  group.item_id,
                                  e.target.value
                                );
                            }
                          : formik.handleChange
                      }
                      variant="outlined"
                      placeholder=""
                      sx={styles.itemTextField}
                      error={
                        formik.touched.items?.[groupIdx]?.item_name &&
                        Boolean(formik.errors.items?.[groupIdx]?.item_name)
                      }
                      required
                    />
                    {formik.touched.items?.[groupIdx]?.item_name &&
                      formik.errors.items?.[groupIdx]?.item_name && (
                        <FormHelperText sx={styles.errorText}>
                          {formik.errors.items?.[groupIdx]?.item_name}
                        </FormHelperText>
                      )}
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12, md: 9.5 }}>
                  <Grid
                    container
                    spacing={3}
                    alignItems="center"
                    size={{ xs: 12 }}
                  >
                    <Grid item size={{ xs: 3.5 }}>
                      <Typography variant="body2" sx={styles.itemLabel}>
                        Description
                      </Typography>
                    </Grid>
                    <Grid item size={{ xs: 3.5 }}>
                      <Typography variant="body2" sx={styles.itemLabel}>
                        Specification
                      </Typography>
                    </Grid>
                    <Grid item size={{ xs: 3.5 }}>
                      <Typography variant="body2" sx={styles.itemLabel}>
                        Tolerance
                      </Typography>
                    </Grid>
                  </Grid>

                  {group?.sub_parts?.map((row, idx) => (
                    <React.Fragment key={idx}>
                      <Grid
                        container
                        spacing={3}
                        alignItems="top"
                        className="subpart-row-hover"
                        mt={0.5}
                      >
                        <Grid item size={{ xs: 3.5 }}>
                          <>
                            <TextField
                              size="small"
                              variant="outlined"
                              sx={styles.itemTextField}
                              fullWidth
                              name={`items[${groupIdx}].sub_parts[${idx}].description`}
                              value={row.description || ""}
                              onChange={
                                initialData?.component_id
                                  ? (e) => {
                                      console.log(row.subpart_id);
                                      formik.setFieldValue(
                                        `items[${groupIdx}].sub_parts[${idx}].description`,
                                        e.target.value
                                      );
                                      row.subpart_id &&
                                        changeSubpartFcn(row.subpart_id, {
                                          description: e.target.value,
                                        });
                                    }
                                  : formik.handleChange
                              }
                              error={
                                formik.touched.items?.[groupIdx]?.sub_parts?.[
                                  idx
                                ]?.description &&
                                Boolean(
                                  formik.errors.items?.[groupIdx]?.sub_parts?.[
                                    idx
                                  ]?.description
                                )
                              }
                              required
                            />
                            {formik.touched.items?.[groupIdx]?.sub_parts?.[idx]
                              ?.description &&
                              formik.errors.items?.[groupIdx]?.sub_parts?.[idx]
                                ?.description && (
                                <FormHelperText sx={styles.errorText}>
                                  {
                                    formik.errors.items?.[groupIdx]
                                      ?.sub_parts?.[idx]?.description
                                  }
                                </FormHelperText>
                              )}
                          </>
                        </Grid>
                        <Grid item size={{ xs: 3.5 }}>
                          <Box>
                            <TextField
                              size="small"
                              variant="outlined"
                              sx={styles.itemTextField}
                              fullWidth
                              name={`items[${groupIdx}].sub_parts[${idx}].specification`}
                              value={row.specification || ""}
                              onChange={
                                initialData?.component_id
                                  ? (e) => {
                                      formik.setFieldValue(
                                        `items[${groupIdx}].sub_parts[${idx}].specification`,
                                        e.target.value
                                      );
                                      row.subpart_id &&
                                        changeSubpartFcn(row.subpart_id, {
                                          specification: e.target.value,
                                        });
                                    }
                                  : formik.handleChange
                              }
                              error={
                                formik.touched.items?.[groupIdx]?.sub_parts?.[
                                  idx
                                ]?.specification &&
                                Boolean(
                                  formik.errors.items?.[groupIdx]?.sub_parts?.[
                                    idx
                                  ]?.specification
                                )
                              }
                              required
                            />
                            {formik.touched.items?.[groupIdx]?.sub_parts?.[idx]
                              ?.specification &&
                              formik.errors.items?.[groupIdx]?.sub_parts?.[idx]
                                ?.specification && (
                                <FormHelperText sx={styles.errorText}>
                                  {
                                    formik.errors.items?.[groupIdx]
                                      ?.sub_parts?.[idx]?.specification
                                  }
                                </FormHelperText>
                              )}
                          </Box>
                        </Grid>
                        <Grid item size={{ xs: 3.5 }}>
                          <Box>
                            <TextField
                              size="small"
                              variant="outlined"
                              sx={styles.itemTextField}
                              fullWidth
                              name={`items[${groupIdx}].sub_parts[${idx}].tolerance`}
                              value={row.tolerance || ""}
                              onChange={
                                initialData?.component_id
                                  ? (e) => {
                                      formik.setFieldValue(
                                        `items[${groupIdx}].sub_parts[${idx}].tolerance`,
                                        e.target.value
                                      );
                                      row.subpart_id &&
                                        changeSubpartFcn(row.subpart_id, {
                                          tolerance: e.target.value,
                                        });
                                    }
                                  : formik.handleChange
                              }
                              error={
                                formik.touched.items?.[groupIdx]?.sub_parts?.[
                                  idx
                                ]?.tolerance &&
                                Boolean(
                                  formik.errors.items?.[groupIdx]?.sub_parts?.[
                                    idx
                                  ]?.tolerance
                                )
                              }
                              required
                            />
                            {formik.touched.items?.[groupIdx]?.sub_parts?.[idx]
                              ?.tolerance &&
                              formik.errors.items?.[groupIdx]?.sub_parts?.[idx]
                                ?.tolerance && (
                                <FormHelperText sx={styles.errorText}>
                                  {
                                    formik.errors.items?.[groupIdx]
                                      ?.sub_parts?.[idx]?.tolerance
                                  }
                                </FormHelperText>
                              )}
                          </Box>
                        </Grid>
                        <Grid item size={{ xs: 1.5 }} sx={{ textAlign: "end" }}>
                          {group.sub_parts.length > 1 && (
                            <span className="subpart-delete-icon">
                              <Tooltip title={`Remove`} arrow placement="top">
                                <IconButtons
                                  buttonType="delete"
                                  onClick={() =>
                                    removeSubPart(groupIdx, idx, row.subpart_id)
                                  }
                                />
                              </Tooltip>
                            </span>
                          )}
                        </Grid>
                      </Grid>
                    </React.Fragment>
                  ))}
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="center" sx={{ mt: 1 }}>
                <Tooltip title={`Add`} arrow placement="top">
                  <IconButtons
                    buttonType="add"
                    onClick={() => addSubPart(groupIdx)}
                  />
                </Tooltip>
              </Box>
            </Box>
          ))}

        <Box display="flex" justifyContent="flex-end" sx={{ mt: 2, mb: 1 }}>
          <AppButton
            buttonType="primary"
            onClick={addItem}
            startIcon={<AddIcon sx={{ color: "#fff" }} />}
            label="Add Item"
          />
        </Box>
      </Box>
    </ModalTemplate>
  );
};

export default CreateTemplateForm;
